import { useCallback, useRef } from 'react';
import { useFetch } from './useFetch';

interface TrackEvent {
  action: string;
  meta_data?: Record<string, any>;
}

// Feature flag para analytics - DESABILITADO até implementação do endpoint
// TODO: Alterar para true quando o endpoint /v1/analytics estiver implementado
let analyticsEnabled = false;
let analyticsCheckAttempted = true; // Já consideramos que verificamos

export const useAnalytics = () => {
  const { requestApi } = useFetch('/v1/analytics');
  const errorCountRef = useRef(0);

  const track = useCallback(
    async (event: TrackEvent) => {
      // Se analytics foi desabilitado após múltiplas falhas, não fazer nada
      if (!analyticsEnabled) {
        return;
      }

      try {
        await requestApi('post', {
          url: '/track',
          data: event,
        });
        // Reset contador de erros em caso de sucesso
        errorCountRef.current = 0;
      } catch (error: any) {
        errorCountRef.current += 1;

        // Se falhar 3 vezes ou retornar 404, desabilitar analytics
        if (errorCountRef.current >= 3 || error?.response?.status === 404) {
          analyticsEnabled = false;
          if (!analyticsCheckAttempted) {
            analyticsCheckAttempted = true;
          }
        }
      }
    },
    [requestApi]
  );

  const trackPageView = useCallback(
    (page: string) => {
      track({
        action: 'page_view',
        meta_data: { page },
      });
    },
    [track]
  );

  const trackModuleView = useCallback(
    (moduleId: number) => {
      track({
        action: 'module_view',
        meta_data: { module_id: moduleId },
      });
    },
    [track]
  );

  const trackWeekView = useCallback(
    (weekId: number) => {
      track({
        action: 'week_view',
        meta_data: { week_id: weekId },
      });
    },
    [track]
  );

  const trackMeetingView = useCallback(
    (weekId: number, meetingId?: number) => {
      track({
        action: 'meeting_view',
        meta_data: { week_id: weekId, meeting_id: meetingId },
      });
    },
    [track]
  );

  const trackMaterialView = useCallback(
    (materialId: number) => {
      track({
        action: 'material_view',
        meta_data: { material_id: materialId },
      });
    },
    [track]
  );

  const trackMaterialDownload = useCallback(
    (materialId: number) => {
      track({
        action: 'material_download',
        meta_data: { material_id: materialId },
      });
    },
    [track]
  );

  return {
    track,
    trackPageView,
    trackModuleView,
    trackWeekView,
    trackMeetingView,
    trackMaterialView,
    trackMaterialDownload,
  };
};
